import { useState } from "react";
import { connect } from 'react-redux';
import { requestResetPassword } from '../../actions/auth';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';

interface Props {
  docType: { id: number; name: string }[];
  requestResetPassword: (data: FormData) => void;
  successPasswordReset: boolean;
  loading: boolean;
  errorPasswordReset: boolean;
  error: any;
}

interface FormData {
  identificationType: number;
  identification: string;
  email: string;
}

const ResetPassword = ({
  docType,
  requestResetPassword,
  successPasswordReset,
  loading,
  errorPasswordReset,
  error
}: Props) => {
  const [formData, setFormData] = useState<FormData>({
    identificationType: 1,
    identification: "",
    email: ""
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Si es identification, permitir solo números
    if (name === "identification") {
      if (!/^\d*$/.test(value)) return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<FormData> = {};
    if (!formData.email) {
      newErrors.email = "El email es obligatorio";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Ingrese un email válido";
    }

    if (!formData.identification) {
      newErrors.identification = "Ingrese un número válido";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      requestResetPassword(formData);
    }
  };

  return (
    <Box display="flex" justifyContent="center" py={6}>
      <Box width={{ xs: '100%', md: '500px' }} px={2}>
        <Box display="flex" justifyContent="center" mb={3}>
          <Box
            width={45}
            height={45}
            bgcolor="#E3000B"
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <LockIcon sx={{ color: "#fff" }} />
          </Box>
        </Box>
        <Typography variant="h5" align="center" mb={4}>
          Recuperar Contraseña
        </Typography>

        <form onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            margin="normal"
          />

          <TextField
            select
            fullWidth
            label="Tipo de documento"
            name="identificationType"
            value={formData.identificationType}
            onChange={handleChange}
            error={!!errors.identificationType}
            helperText={errors.identificationType}
            margin="normal"
          >
            {docType?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Número de documento (sin puntos ni guiones)"
            name="identification"
            value={formData.identification}
            onChange={handleChange}
            error={!!errors.identification}
            helperText={errors.identification}
            margin="normal"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />

          <Box mt={3}>
            <Button
              variant="contained"
              color="error"
              type="submit"
              fullWidth
              disabled={loading}
              sx={{ py: 1.5 }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Recuperar contraseña"}
            </Button>
          </Box>

          {successPasswordReset && (
            <Alert severity="success" sx={{ mt: 3 }}>
              Por favor verifique su casilla de email para recuperar la contraseña
            </Alert>
          )}

          {errorPasswordReset && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error?.response?.data?.message || "Ocurrió un error"}
            </Alert>
          )}
        </form>
      </Box>
    </Box>
  );
};

export default connect(
  (state: any) => ({
    docType: state.params.docType,
    successPasswordReset: state.auth.successPasswordReset,
    loading: state.auth.loading,
    errorPasswordReset: state.auth.errorPasswordReset,
    error: state.auth.error
  }),
  (dispatch: any) => ({
    requestResetPassword: (data: FormData) => dispatch(requestResetPassword(data))
  })
)(ResetPassword);